import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const EpidemicSimulator = () => {
  // 模拟参数
  const [params, setParams] = useState({
    population: 1000,
    dangerRadius: 3,
    infectionRate: 0.3,
    recoveryRate: 0.98,
    exposePeriod: 7,
    removePeriod: 14,
    isolationDay: 3,
    isolationStart: 20,
    isolationRate: 0.99,
    complianceRate: 0.95, // 新增：全员隔离遵守率
  });

  // 模型选择
  const [modelType, setModelType] = useState("SEIR");

  // 模拟状态
  const [isRunning, setIsRunning] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1); // 新增：模拟速度
  const [day, setDay] = useState(0);
  const [frameCount, setFrameCount] = useState(0); // 新增：帧计数器
  const [people, setPeople] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [currentStats, setCurrentStats] = useState({
    S: params.population - 1,
    E: 0,
    I: 1,
    R: 0,
    D: 0,
    Isolated: 0,
  });

  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const statsRef = useRef({
    S: params.population - 1,
    E: 0,
    I: 1,
    R: 0,
    D: 0,
    Isolated: 0,
  });
  const lastRecordedDayRef = useRef(-1); // 使用ref而不是state来跟踪

  // 每天的帧数，用于控制时间流逝
  const FRAMES_PER_DAY = 20;

  // 居家区域配置 - 调整为更小的网格以容纳更多人
  const HOME_GRID_SIZE = 25; // 减小网格大小
  const HOMES_PER_ROW = Math.floor(800 / HOME_GRID_SIZE);
  const HOMES_PER_COL = Math.floor(600 / HOME_GRID_SIZE);

  // 初始化人群
  const initializePeople = useCallback(() => {
    const newPeople = [];
    for (let i = 0; i < params.population; i++) {
      // 为每个人分配一个居家区域
      const homeCol = i % HOMES_PER_ROW;
      const homeRow = Math.floor(i / HOMES_PER_ROW);
      const homeX = homeCol * HOME_GRID_SIZE;
      const homeY = homeRow * HOME_GRID_SIZE;

      newPeople.push({
        id: i,
        x: Math.random() * 800,
        y: Math.random() * 600,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4,
        state: i === 0 ? "I" : "S", // 第一个人感染
        exposeDays: 0,
        infectDays: 0,
        isIsolated: false,
        // 居家区域坐标和尺寸
        homeX: homeX,
        homeY: homeY,
        homeWidth: HOME_GRID_SIZE,
        homeHeight: HOME_GRID_SIZE,
        // 是否遵守全员隔离规定
        isCompliant: Math.random() < params.complianceRate,
      });
    }
    setPeople(newPeople);
    setDay(0);
    setFrameCount(0);
    lastRecordedDayRef.current = -1; // 重置ref
    setStatistics([]);
    statsRef.current = {
      S: params.population - 1,
      E: 0,
      I: 1,
      R: 0,
      D: 0,
      Isolated: 0,
    };
    setCurrentStats(statsRef.current);
  }, [params.population, params.complianceRate]);

  // 计算距离
  const getDistance = (p1, p2) => {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // 更新单个个体状态
  const updatePersonState = useCallback(
    (person, allPeople, currentDay, isNewDay) => {
      const newPerson = { ...person };

      // 移动逻辑 - 全员隔离时实现居家隔离
      if (!newPerson.isIsolated) {
        if (
          modelType === "SEIR_Lockdown" &&
          currentDay >= params.isolationStart
        ) {
          if (newPerson.isCompliant) {
            // 遵守隔离规定的人：在居家区域内活动，速度变慢

            // 如果刚开始隔离，将人移动到他们的居家区域内
            if (currentDay === params.isolationStart && isNewDay) {
              newPerson.x =
                newPerson.homeX + Math.random() * (newPerson.homeWidth - 4) + 2;
              newPerson.y =
                newPerson.homeY +
                Math.random() * (newPerson.homeHeight - 4) +
                2;
              newPerson.vx = (Math.random() - 0.5) * 2; // 降低速度
              newPerson.vy = (Math.random() - 0.5) * 2;
            }

            newPerson.x += newPerson.vx * 0.3;
            newPerson.y += newPerson.vy * 0.3;

            // 居家区域边界检查和反弹
            const homeLeft = newPerson.homeX + 1;
            const homeRight = newPerson.homeX + newPerson.homeWidth - 1;
            const homeTop = newPerson.homeY + 1;
            const homeBottom = newPerson.homeY + newPerson.homeHeight - 1;

            if (newPerson.x <= homeLeft || newPerson.x >= homeRight) {
              newPerson.vx *= -1;
              newPerson.x = Math.max(
                homeLeft,
                Math.min(homeRight, newPerson.x)
              );
            }
            if (newPerson.y <= homeTop || newPerson.y >= homeBottom) {
              newPerson.vy *= -1;
              newPerson.y = Math.max(
                homeTop,
                Math.min(homeBottom, newPerson.y)
              );
            }
          } else {
            // 不遵守规定的人：正常活动，但速度稍微变慢
            newPerson.x += newPerson.vx * 0.7;
            newPerson.y += newPerson.vy * 0.7;

            // 全区域边界反弹
            if (newPerson.x <= 0 || newPerson.x >= 800) {
              newPerson.vx *= -1;
              newPerson.x = Math.max(1, Math.min(799, newPerson.x));
            }
            if (newPerson.y <= 0 || newPerson.y >= 600) {
              newPerson.vy *= -1;
              newPerson.y = Math.max(1, Math.min(599, newPerson.y));
            }
          }
        } else {
          // 正常移动
          newPerson.x += newPerson.vx;
          newPerson.y += newPerson.vy;

          // 全区域边界反弹
          if (newPerson.x <= 0 || newPerson.x >= 800) {
            newPerson.vx *= -1;
            newPerson.x = Math.max(1, Math.min(799, newPerson.x));
          }
          if (newPerson.y <= 0 || newPerson.y >= 600) {
            newPerson.vy *= -1;
            newPerson.y = Math.max(1, Math.min(599, newPerson.y));
          }
        }
      }

      // 感染检查 - 改进全员隔离时的感染逻辑
      if (newPerson.state === "S") {
        for (let other of allPeople) {
          if (
            (other.state === "I" ||
              (modelType.includes("E") && other.state === "E")) &&
            getDistance(newPerson, other) < params.dangerRadius
          ) {
            let infectionRate = params.infectionRate;

            // 全员隔离时的感染概率逻辑
            if (
              modelType === "SEIR_Lockdown" &&
              currentDay >= params.isolationStart
            ) {
              // 判断感染者和易感者是否都在遵守隔离
              const bothCompliant = newPerson.isCompliant && other.isCompliant;
              const bothViolating =
                !newPerson.isCompliant && !other.isCompliant;
              const oneViolating = newPerson.isCompliant !== other.isCompliant;

              if (bothCompliant) {
                // 两人都居家隔离：极低感染概率（几乎不接触）
                infectionRate *= 0.05;
              } else if (oneViolating) {
                // 一人违规一人居家：中等感染概率（违规者可能接触居家者）
                infectionRate *= 0.3;
              } else if (bothViolating) {
                // 两人都违规：正常感染概率（自由活动）
                infectionRate *= 1.0;
              }
            }

            if (Math.random() < infectionRate) {
              if (modelType.includes("E")) {
                newPerson.state = "E";
              } else {
                newPerson.state = "I";
              }
              break;
            }
          }
        }
      }

      // 状态转换 - 只在新的一天时检查状态转换
      if (isNewDay) {
        switch (newPerson.state) {
          case "E":
            newPerson.exposeDays++;
            if (newPerson.exposeDays > params.exposePeriod) {
              if (Math.random() < 0.8) {
                // E_I_Rate = 0.8，发展为有症状感染者
                newPerson.state = "I";
                newPerson.exposeDays = 0; // 重置暴露天数
              } else {
                newPerson.state = "R"; // 无症状康复，直接获得免疫
                newPerson.exposeDays = 0;
              }
            }
            break;

          case "I":
            newPerson.infectDays++;

            // 个体隔离检查（仅针对SEIR_Isolation模式）
            if (
              modelType === "SEIR_Isolation" &&
              currentDay > params.isolationStart &&
              newPerson.infectDays > params.isolationDay &&
              !newPerson.isIsolated &&
              Math.random() < params.isolationRate
            ) {
              newPerson.isIsolated = true;
              newPerson.x = 820 + Math.random() * 80;
              newPerson.y = 50 + Math.random() * 500;
              newPerson.vx = 0;
              newPerson.vy = 0;
            }

            // 康复或死亡检查
            if (newPerson.infectDays > params.removePeriod) {
              if (Math.random() < params.recoveryRate) {
                newPerson.state = "R";
                newPerson.infectDays = 0; // 重置感染天数
                if (newPerson.isIsolated) {
                  newPerson.x = Math.random() * 800;
                  newPerson.y = Math.random() * 600;
                  newPerson.vx = (Math.random() - 0.5) * 4;
                  newPerson.vy = (Math.random() - 0.5) * 4;
                  newPerson.isIsolated = false;
                }
              } else {
                newPerson.state = "D";
                newPerson.vx = 0;
                newPerson.vy = 0;
                newPerson.infectDays = 0;
              }
            }
            break;
        }
      }

      return newPerson;
    },
    [params, modelType]
  );

  // 绘制函数
  const draw = useCallback(
    (peopleArray) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 绘制主区域边界
      ctx.strokeStyle = "#ccc";
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, 800, 600);

      // 绘制全员隔离的居家网格（仅在全员隔离模式下且隔离开始后显示）
      if (modelType === "SEIR_Lockdown" && day >= params.isolationStart) {
        ctx.strokeStyle = "#e0e0e0";
        ctx.lineWidth = 1;
        // 绘制垂直线
        for (let x = HOME_GRID_SIZE; x < 800; x += HOME_GRID_SIZE) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, 600);
          ctx.stroke();
        }
        // 绘制水平线
        for (let y = HOME_GRID_SIZE; y < 600; y += HOME_GRID_SIZE) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(800, y);
          ctx.stroke();
        }

        // 高亮显示网格使用情况
        ctx.fillStyle = "rgba(200, 220, 255, 0.3)"; // 蓝色：遵守规定的人的网格
        const violatorGrids = new Set(); // 记录违规者的网格

        peopleArray.forEach((person) => {
          if (person.isCompliant) {
            // 遵守规定的人：蓝色高亮网格
            ctx.fillRect(
              person.homeX,
              person.homeY,
              person.homeWidth,
              person.homeHeight
            );
          } else {
            // 记录违规者的网格位置
            violatorGrids.add(`${person.homeX}-${person.homeY}`);
          }
        });

        // 用浅灰色标记违规者分配的网格
        ctx.fillStyle = "rgba(220, 220, 220, 0.2)"; // 浅灰色：违规者分配的网格
        violatorGrids.forEach((gridKey) => {
          const [x, y] = gridKey.split("-").map(Number);
          ctx.fillRect(x, y, HOME_GRID_SIZE, HOME_GRID_SIZE);
        });
      }

      // 绘制个体隔离区域（仅在个体隔离模式下显示）
      if (modelType === "SEIR_Isolation") {
        ctx.strokeStyle = "#ff9999";
        ctx.strokeRect(820, 50, 80, 500);
        ctx.fillStyle = "#ffeeee";
        ctx.fillRect(820, 50, 80, 500);
      }

      // 绘制个体
      peopleArray.forEach((person) => {
        let color;
        let size = 3;

        switch (person.state) {
          case "S":
            color = "#0066ff";
            size = 2;
            break;
          case "E":
            color = "#ffaa00";
            size = 3;
            break;
          case "I":
            color = "#ff0000";
            size = 4;
            break;
          case "R":
            color = "#00ff00";
            size = 3;
            break;
          case "D":
            color = "#000000";
            size = 3;
            break;
          default:
            color = "#666666";
        }

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(person.x, person.y, size, 0, 2 * Math.PI);
        ctx.fill();

        // 全员隔离时，为不遵守规定的人添加细的半透明紫色边框
        if (
          modelType === "SEIR_Lockdown" &&
          day >= params.isolationStart &&
          person.isCompliant === false
        ) {
          ctx.strokeStyle = "rgba(255, 0, 255, 0.6)"; // 半透明紫色边框
          ctx.lineWidth = 1; // 更细的边框
          ctx.beginPath();
          ctx.arc(person.x, person.y, size + 0.5, 0, 2 * Math.PI);
          ctx.stroke();
        }
      });

      // 绘制统计信息
      ctx.fillStyle = "#000";
      ctx.font = "16px Arial";
      ctx.fillText(
        `Day: ${Math.floor(day)} (Speed: ${simulationSpeed}x) ${
          isRunning ? "▶运行中" : "⏸已暂停"
        }`,
        10,
        25
      );
      ctx.fillText(`S: ${statsRef.current.S}`, 10, 45);
      if (modelType.includes("E"))
        ctx.fillText(`E: ${statsRef.current.E}`, 10, 65);
      ctx.fillText(
        `I: ${statsRef.current.I}`,
        10,
        modelType.includes("E") ? 85 : 65
      );
      ctx.fillText(
        `R: ${statsRef.current.R}`,
        10,
        modelType.includes("E") ? 105 : 85
      );
      ctx.fillText(
        `D: ${statsRef.current.D}`,
        10,
        modelType.includes("E") ? 125 : 105
      );
      if (modelType === "SEIR_Isolation")
        ctx.fillText(`Isolated: ${statsRef.current.Isolated}`, 10, 145);

      // 显示隔离信息
      if (modelType === "SEIR_Lockdown" && day >= params.isolationStart) {
        const compliantCount = peopleArray.filter(
          (p) => p.isCompliant === true
        ).length;
        const nonCompliantCount = peopleArray.filter(
          (p) => p.isCompliant === false
        ).length;
        ctx.fillText(
          `居家: ${compliantCount} | 违规: ${nonCompliantCount}`,
          10,
          165
        );
        ctx.fillText(
          `遵守率: ${(
            (compliantCount / (compliantCount + nonCompliantCount)) *
            100
          ).toFixed(1)}%`,
          10,
          185
        );
        // ctx.font = "12px Arial";
        // ctx.fillText(
        //   `感染规则: 居家↔居家(5%) | 居家↔违规(30%) | 违规↔违规(100%)`,
        //   10,
        //   205
        // );
        ctx.font = "16px Arial"; // 恢复字体大小
      }
    },
    [day, modelType, simulationSpeed, params.isolationStart]
  );

  // 模拟步进
  const simulationStep = useCallback(() => {
    setFrameCount((prevFrameCount) => {
      const newFrameCount = prevFrameCount + 1;
      const isNewDay = newFrameCount % FRAMES_PER_DAY === 0;
      const currentDay = Math.floor(newFrameCount / FRAMES_PER_DAY);

      // 只在新的一天时更新天数显示
      if (isNewDay) {
        setDay(currentDay);
      }

      setPeople((currentPeople) => {
        const newPeople = currentPeople.map((person) =>
          updatePersonState(person, currentPeople, currentDay, isNewDay)
        );

        // 统计各状态人数
        const newStats = { S: 0, E: 0, I: 0, R: 0, D: 0, Isolated: 0 };
        newPeople.forEach((person) => {
          if (person.isIsolated) {
            newStats.Isolated++;
          } else {
            newStats[person.state]++;
          }
        });

        // 更新实时统计（用于显示）
        statsRef.current = newStats;
        setCurrentStats({ ...newStats });

        // 关键修复：使用ref确保不重复添加数据点
        if (isNewDay && currentDay > lastRecordedDayRef.current) {
          lastRecordedDayRef.current = currentDay;
          setStatistics((prev) => [
            ...prev,
            {
              day: currentDay,
              ...newStats,
            },
          ]);
        }

        draw(newPeople);
        return newPeople;
      });

      return newFrameCount;
    });
  }, [draw, updatePersonState]);

  // 动画循环
  useEffect(() => {
    if (isRunning) {
      const interval = Math.floor(50 / simulationSpeed); // 根据速度调整间隔
      animationRef.current = setInterval(simulationStep, interval);
    } else {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [isRunning, simulationStep, simulationSpeed]);

  // 初始化
  useEffect(() => {
    initializePeople();
  }, [initializePeople, modelType]);

  // 当遵守率参数改变时，重新分配遵守状态
  useEffect(() => {
    if (modelType === "SEIR_Lockdown") {
      setPeople((currentPeople) =>
        currentPeople.map((person) => ({
          ...person,
          isCompliant: Math.random() < params.complianceRate,
        }))
      );
    }
  }, [params.complianceRate, modelType]);

  // 初始绘制
  useEffect(() => {
    draw(people);
  }, [people, draw]);

  const handleStart = () => setIsRunning(true);
  const handleStop = () => setIsRunning(false);
  const handleReset = () => {
    setIsRunning(false);
    initializePeople();
  };

  const handleParamChange = (key, value) => {
    setParams((prev) => ({ ...prev, [key]: parseFloat(value) }));
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-4 text-center">新冠传播模拟器</h1>

      {/* 控制面板 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* 模型选择 */}
        <div className="border p-4 rounded">
          <h3 className="font-semibold mb-2">模型选择</h3>
          <select
            value={modelType}
            onChange={(e) => setModelType(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="SI">SI模型</option>
            <option value="SIR">SIR模型</option>
            <option value="SEIR">SEIR模型</option>
            <option value="SEIR_Isolation">SEIR + 隔离</option>
            <option value="SEIR_Lockdown">SEIR + 全员隔离</option>
          </select>
        </div>

        {/* 控制按钮 */}
        <div className="border p-4 rounded">
          <h3 className="font-semibold mb-2">模拟控制</h3>
          <div className="space-y-3">
            <div className="flex space-x-2">
              <button
                onClick={handleStart}
                disabled={isRunning}
                className={`px-4 py-2 rounded font-medium transition-all duration-200 transform ${
                  isRunning
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600 active:bg-green-700 text-white hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                }`}
              >
                {isRunning ? "运行中..." : "开始"}
              </button>
              <button
                onClick={handleStop}
                disabled={!isRunning}
                className={`px-4 py-2 rounded font-medium transition-all duration-200 transform ${
                  !isRunning
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 active:bg-red-700 text-white hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                }`}
              >
                {!isRunning ? "已暂停" : "暂停"}
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white rounded font-medium transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
              >
                重置
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                模拟速度: {simulationSpeed}x
              </label>
              <input
                type="range"
                min="0.5"
                max="5"
                step="0.5"
                value={simulationSpeed}
                onChange={(e) => setSimulationSpeed(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 mt-1">
                0.5x (慢) - 5x (快)
              </div>
            </div>
          </div>
        </div>

        {/* 当前统计 */}
        <div className="border p-4 rounded">
          <h3 className="font-semibold mb-2">
            当前状态
            <span
              className={`ml-2 px-2 py-1 text-xs rounded ${
                isRunning
                  ? "bg-green-100 text-green-600"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {isRunning ? "▶运行中" : "⏸已暂停"}
            </span>
          </h3>
          <div className="text-sm space-y-1">
            <div>
              易感者: <span className="text-blue-600">{currentStats.S}</span>
            </div>
            {modelType.includes("E") && (
              <div>
                潜伏者:{" "}
                <span className="text-orange-600">{currentStats.E}</span>
              </div>
            )}
            <div>
              感染者: <span className="text-red-600">{currentStats.I}</span>
            </div>
            <div>
              康复者: <span className="text-green-600">{currentStats.R}</span>
            </div>
            <div>
              死亡者: <span className="text-black">{currentStats.D}</span>
            </div>
            {modelType.includes("Isolation") && (
              <div>
                隔离者:{" "}
                <span className="text-purple-600">{currentStats.Isolated}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 参数设置 */}
      <div className="border p-4 rounded mb-4">
        <h3 className="font-semibold mb-2">参数设置</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm">人口数量</label>
            <input
              type="number"
              value={params.population}
              onChange={(e) => handleParamChange("population", e.target.value)}
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">感染率</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={params.infectionRate}
              onChange={(e) =>
                handleParamChange("infectionRate", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">康复率</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={params.recoveryRate}
              onChange={(e) =>
                handleParamChange("recoveryRate", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">潜伏期天数</label>
            <input
              type="number"
              value={params.exposePeriod}
              onChange={(e) =>
                handleParamChange("exposePeriod", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">康复天数</label>
            <input
              type="number"
              value={params.removePeriod}
              onChange={(e) =>
                handleParamChange("removePeriod", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">危险距离</label>
            <input
              type="number"
              value={params.dangerRadius}
              onChange={(e) =>
                handleParamChange("dangerRadius", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm">隔离开始天数</label>
            <input
              type="number"
              value={params.isolationStart}
              onChange={(e) =>
                handleParamChange("isolationStart", e.target.value)
              }
              className="w-full p-1 border rounded"
            />
          </div>
          {modelType === "SEIR_Lockdown" && (
            <div>
              <label className="block text-sm">隔离遵守率</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={params.complianceRate}
                onChange={(e) =>
                  handleParamChange("complianceRate", e.target.value)
                }
                className="w-full p-1 border rounded"
              />
              <div className="text-xs text-gray-500 mt-1">
                {(params.complianceRate * 100).toFixed(0)}% 遵守 |{" "}
                {((1 - params.complianceRate) * 100).toFixed(0)}% 违规
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 可视化区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 模拟画布 */}
        <div className="border rounded p-2">
          <h3 className="font-semibold mb-2">
            实时模拟 (Day: {Math.floor(day)})
          </h3>
          <canvas
            ref={canvasRef}
            width={modelType.includes("Isolation") ? 920 : 800}
            height={600}
            className="border w-full"
            style={{ maxWidth: "100%", height: "auto" }}
          />
          <div className="mt-2 text-sm">
            <span className="inline-block w-3 h-3 bg-blue-500 mr-1"></span>
            易感者
            {modelType.includes("E") && (
              <>
                <span className="inline-block w-3 h-3 bg-orange-500 mr-1 ml-3"></span>
                潜伏者
              </>
            )}
            <span className="inline-block w-3 h-3 bg-red-500 mr-1 ml-3"></span>
            感染者
            <span className="inline-block w-3 h-3 bg-green-500 mr-1 ml-3"></span>
            康复者
            <span className="inline-block w-3 h-3 bg-black mr-1 ml-3"></span>
            死亡者
            {modelType === "SEIR_Lockdown" && day >= params.isolationStart && (
              <>
                <br />
                <span
                  className="inline-block w-3 h-3 border border-purple-400 mr-1 mt-1"
                  style={{ backgroundColor: "rgba(255,0,255,0.1)" }}
                ></span>
                <span className="text-purple-600">违规者（细紫边框）</span>
                {/* <br /> */}
                <span
                  className="inline-block w-4 h-3 mr-1 mt-1"
                  style={{
                    backgroundColor: "rgba(200,220,255,0.3)",
                    border: "1px solid #ccc",
                  }}
                ></span>
                <span className="text-blue-600">居家网格（蓝色）</span>
                <span
                  className="inline-block w-4 h-3 mr-1 ml-3 mt-1"
                  style={{
                    backgroundColor: "rgba(220,220,220,0.2)",
                    border: "1px solid #ccc",
                  }}
                ></span>
                {/* <span className="text-gray-600">违规者网格（灰色）</span> */}
                {/* <br /> */}
                {/* <span className="text-xs text-gray-600 mt-1">
                  感染规则：居家↔居家(5%) | 违规↔居家(30%) | 违规↔违规(100%)
                </span> */}
              </>
            )}
          </div>
        </div>

        {/* 统计图表 */}
        <div className="border rounded p-2">
          <h3 className="font-semibold mb-2">人数变化趋势</h3>
          <div style={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={statistics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="S"
                  stroke="#0066ff"
                  name="易感者"
                  strokeWidth={2}
                />
                {modelType.includes("E") && (
                  <Line
                    type="monotone"
                    dataKey="E"
                    stroke="#ffaa00"
                    name="潜伏者"
                    strokeWidth={2}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="I"
                  stroke="#ff0000"
                  name="感染者"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="R"
                  stroke="#00ff00"
                  name="康复者"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="D"
                  stroke="#000000"
                  name="死亡者"
                  strokeWidth={2}
                />
                {modelType.includes("Isolation") && (
                  <Line
                    type="monotone"
                    dataKey="Isolated"
                    stroke="#9900ff"
                    name="隔离者"
                    strokeWidth={2}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default EpidemicSimulator;
